import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Send, MessageSquare, CheckCircle2, ImageIcon, Loader2, X, FileCheck2 } from 'lucide-react';

/**
 * Renders an append-only timeline of every approval event for one proposal item:
 *   sent → changes_requested → resent → … → approved
 *
 * Each event references an immutable `mockup_snapshot` doc. The "Approved" event
 * keeps the exact design version that was signed off on, even if the designer
 * later edits the mockup.
 *
 * Props:
 *   proposalId, itemIndex   — which item to load
 *   publicToken             — if provided, uses the public (customer-side) endpoints
 */
const ICON_BY_TYPE = {
  sent: <Send size={14} />,
  changes_requested: <MessageSquare size={14} />,
  approved: <CheckCircle2 size={14} />,
};

const COLOR_BY_TYPE = {
  sent: { bg: '#dbeafe', fg: '#1e40af', dot: '#3b82f6' },
  changes_requested: { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
  approved: { bg: '#dcfce7', fg: '#166534', dot: '#16a34a' },
};

const LABEL_BY_TYPE = {
  sent: 'Sent for approval',
  changes_requested: 'Changes requested',
  approved: 'Approved',
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const MockupApprovalTimeline = ({ proposalId, itemIndex, publicToken = null }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openSnapshot, setOpenSnapshot] = useState(null); // full snapshot for modal
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const isPublic = !!publicToken;

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const url = isPublic
          ? `/public/proposals/${proposalId}/items/${itemIndex}/approval-history?token=${encodeURIComponent(publicToken)}`
          : `/proposals/${proposalId}/items/${itemIndex}/approval-history`;
        const res = await axios.get(url);
        if (!cancelled) setData(res.data);
      } catch (e) {
        // Silently ignore — older proposals may not have history yet.
        if (!cancelled) setData({ history: [], snapshots: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [proposalId, itemIndex, publicToken, isPublic]);

  const viewSnapshot = async (snapshotId) => {
    if (!snapshotId) return;
    try {
      setSnapshotLoading(true);
      const url = isPublic
        ? `/public/mockup-snapshots/${snapshotId}?proposal_id=${encodeURIComponent(proposalId)}&token=${encodeURIComponent(publicToken)}`
        : `/mockup-snapshots/${snapshotId}`;
      const res = await axios.get(url);
      setOpenSnapshot(res.data);
    } catch {
      // ignore
    } finally {
      setSnapshotLoading(false);
    }
  };

  const downloadCertificate = async () => {
    // Admin-only — the public token can't authorise PDF certificate generation
    if (isPublic) return;
    try {
      const res = await axios.get(
        `/proposals/${proposalId}/items/${itemIndex}/approval-certificate.pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approval-certificate-item${itemIndex + 1}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) {
      // Silent
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '10px 0', color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Loader2 size={12} className="animate-spin" /> Loading approval history…
      </div>
    );
  }

  const history = (data && data.history) || [];
  const snapshots = (data && data.snapshots) || {};
  const approvedSnapId = data?.approved_snapshot_id;

  if (history.length === 0) {
    return (
      <div
        data-testid={`approval-timeline-empty-${itemIndex}`}
        style={{
          padding: '10px 12px',
          background: '#f8fafc',
          borderRadius: 8,
          fontSize: 12,
          color: '#64748b',
          border: '1px dashed #e2e8f0',
        }}
      >
        No approval activity yet. Send the mockup to the customer to start the history.
      </div>
    );
  }

  // Count how many "sent" events we've seen so far, so we can label "v1, v2…"
  let sentCount = 0;

  return (
    <div data-testid={`approval-timeline-${itemIndex}`} style={{ marginTop: 4 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginBottom: 8, flexWrap: 'wrap',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: '#0f172a',
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          Mockup Approval History
        </div>
        {data?.current_status === 'approved' && !isPublic && (
          <button
            type="button"
            onClick={downloadCertificate}
            data-testid={`download-certificate-${itemIndex}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#16a34a', color: 'white', border: 'none',
              padding: '6px 12px', borderRadius: 6,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              letterSpacing: 0.3,
            }}
          >
            <FileCheck2 size={13} /> Approval Certificate (PDF)
          </button>
        )}
      </div>
      <ol
        style={{
          listStyle: 'none', padding: 0, margin: 0,
          borderLeft: '2px solid #e2e8f0', marginLeft: 6,
        }}
      >
        {history.map((ev, i) => {
          const color = COLOR_BY_TYPE[ev.type] || { bg: '#e2e8f0', fg: '#475569', dot: '#94a3b8' };
          const icon = ICON_BY_TYPE[ev.type];
          if (ev.type === 'sent') sentCount += 1;
          const version = ev.type === 'sent' ? sentCount : null;
          const snap = ev.snapshot_id ? snapshots[ev.snapshot_id] : null;
          const isApprovedVersion = ev.type === 'approved' && approvedSnapId && ev.snapshot_id === approvedSnapId;
          return (
            <li key={i} style={{ position: 'relative', paddingLeft: 18, paddingBottom: 14 }}>
              {/* Dot */}
              <span
                style={{
                  position: 'absolute', left: -7, top: 4,
                  width: 12, height: 12, borderRadius: '50%',
                  background: color.dot, border: '2px solid white',
                  boxShadow: '0 0 0 1px ' + color.dot,
                }}
              />
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: color.bg, color: color.fg,
                  padding: '2px 8px', borderRadius: 9999,
                  fontSize: 11, fontWeight: 700,
                }}>
                  {icon}
                  {LABEL_BY_TYPE[ev.type] || ev.type}
                  {version != null ? ` · v${version}` : ''}
                </span>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {formatTimestamp(ev.at)}
                </span>
                {ev.by && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    by <strong>{ev.actor_role === 'admin' ? `${ev.by} (admin)` : ev.by}</strong>
                  </span>
                )}
                {isApprovedVersion && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#166534',
                    background: '#bbf7d0', padding: '1px 6px', borderRadius: 4,
                  }}>
                    LOCKED VERSION
                  </span>
                )}
              </div>
              {ev.feedback && (
                <div style={{
                  marginTop: 6, padding: '8px 10px',
                  background: '#fffbeb', borderLeft: '3px solid #f59e0b',
                  borderRadius: 4, fontSize: 12, color: '#78350f',
                }}>
                  “{ev.feedback}”
                </div>
              )}
              {ev.note && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                  {ev.note}
                </div>
              )}
              {ev.snapshot_id && snap && (ev.type === 'sent' || ev.type === 'approved') && (
                <button
                  type="button"
                  onClick={() => viewSnapshot(ev.snapshot_id)}
                  data-testid={`view-snapshot-btn-${itemIndex}-${i}`}
                  style={{
                    marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'transparent', border: '1px solid #cbd5e1',
                    color: '#475569', padding: '3px 8px', borderRadius: 6,
                    fontSize: 11, cursor: 'pointer',
                  }}
                >
                  <ImageIcon size={11} /> View design{version != null ? ` v${version}` : ''}
                </button>
              )}
            </li>
          );
        })}
      </ol>

      {/* Snapshot viewer modal */}
      {(openSnapshot || snapshotLoading) && (
        <div
          onClick={() => setOpenSnapshot(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
          data-testid="snapshot-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 14,
              width: 'min(900px, 100%)', maxHeight: '92vh', overflow: 'auto',
              padding: 20,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>DESIGN SNAPSHOT</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  Captured {openSnapshot ? formatTimestamp(openSnapshot.created_at) : ''}
                </div>
              </div>
              <button
                onClick={() => setOpenSnapshot(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}
                data-testid="close-snapshot-modal"
              >
                <X size={20} />
              </button>
            </div>
            {snapshotLoading && !openSnapshot && (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                <Loader2 className="animate-spin" /> Loading…
              </div>
            )}
            {openSnapshot && (
              <SnapshotViews snapshot={openSnapshot} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SnapshotViews = ({ snapshot }) => {
  const views = snapshot.mockup_views || {};
  const available = ['front', 'back', 'left', 'right'].filter((v) => views[v]);
  if (available.length === 0 && snapshot.mockup_url) {
    return (
      <img src={snapshot.mockup_url} alt="Snapshot" style={{ maxWidth: '100%', borderRadius: 8 }} />
    );
  }
  if (available.length === 0) {
    return <div style={{ color: '#64748b' }}>No image data was saved with this snapshot.</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: available.length > 1 ? '1fr 1fr' : '1fr', gap: 12 }}>
      {available.map((v) => (
        <div key={v} style={{ background: '#f8fafc', borderRadius: 8, padding: 8, textAlign: 'center' }}>
          <img
            src={views[v]}
            alt={v}
            style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 4 }}
          />
          <div style={{ fontSize: 11, color: '#475569', marginTop: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {v === 'left' ? 'Left Sleeve' : v === 'right' ? 'Right Sleeve' : v}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MockupApprovalTimeline;
